import React, { useState, useEffect } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Stack, Typography, Paper, Chip, IconButton,
  TextField, Button, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert,
  alpha, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useApi } from '../../hooks/useApi';

/**
 * Editor for accountprofile entries that apply to the currently-selected audit.
 *
 * Queries the accountprofile container with:
 *   - account = selectedAuditDetails.account
 *   - audit_function_path = selectedAuditDetails.functionPath
 *
 * The cmapy GET route matches by audit_function_paths membership OR scope='always'
 * so the editor surfaces both targeted notes and global notes for this customer.
 *
 * Used inside the Auditor blade on /audit/findings, alongside the existing
 * "Auditor Override" accordion. The override is per-run; the profile is persistent.
 */
function AccountProfileEditor({ account, customer, functionPath }) {
  const jApi = useApi();
  const theme = useTheme();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // null | { id?, topic, tags, content, scope, audit_function_paths }
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Plain function, not useCallback — useApi() returns a fresh object each
  // render, so depending on jApi inside a useCallback would re-fire load()
  // every render and pin the API into an infinite loop.
  const load = async () => {
    if (!account || !functionPath) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ account, audit_function_path: functionPath });
      const resp = await jApi.request('GET', `cma/accountprofile?${params.toString()}`);
      setRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (ex) {
      setToast({ severity: 'error', text: ex?.toString?.() || 'Failed to load account profile' });
    } finally {
      setIsLoading(false);
    }
  };

  // Only fetch when the actual inputs change — not when load's identity does.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, functionPath]);

  const openNew = () => setEditing({
    topic:                '',
    tags:                 '',
    content:              '',
    scope:                'matched',
    audit_function_paths: functionPath ? [functionPath] : [],
  });

  const openEdit = (row) => setEditing({
    id:                   row.id,
    topic:                row.topic || '',
    tags:                 Array.isArray(row.tags) ? row.tags.join(', ') : '',
    content:              row.content || '',
    scope:                row.scope || 'matched',
    audit_function_paths: row.audit_function_paths || [],
  });

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const tagsArr = editing.tags
        ? editing.tags.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const body = {
        account,
        topic:                editing.topic,
        content:              editing.content,
        scope:                editing.scope,
        tags:                 tagsArr,
        audit_function_paths: editing.audit_function_paths || [],
      };
      if (editing.id) body.id = editing.id;

      const method = editing.id ? 'PATCH' : 'POST';
      await jApi.request(method, 'cma/accountprofile', body);

      setToast({ severity: 'success', text: editing.id ? 'Profile updated' : 'Profile added' });
      closeEdit();
      load();
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Save failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete profile entry "${row.topic}"?`)) return;
    try {
      const params = new URLSearchParams({ account: row.account });
      await jApi.request('DELETE', `cma/accountprofile/${row.id}?${params.toString()}`);
      setToast({ severity: 'success', text: 'Profile deleted' });
      load();
    } catch (ex) {
      setToast({ severity: 'error', text: ex?.toString?.() || 'Delete failed' });
    }
  };

  return (
    <>
      <Accordion
        sx={{
          mb: 2,
          bgcolor: alpha(theme.palette.secondary.main, 0.05),
          '&:before': { display: 'none' },
          boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.12) },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
              📌 Account Profile
            </Typography>
            <Chip
              size="small"
              label={`${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`}
              color={rows.length > 0 ? 'secondary' : 'default'}
              variant={rows.length > 0 ? 'filled' : 'outlined'}
            />
            <Typography variant="caption" color="text.secondary">
              Persistent context for {customer || account}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              Notes, facts, or decisions about this customer that the AI auditor can't infer from data.
              Entries shown here are injected into the prompt for this audit (and other matched audits).
            </Typography>

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {!isLoading && rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No profile entries for this audit yet. Add one to capture context the AI should know.
              </Typography>
            )}

            {!isLoading && rows.map(row => (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                  borderColor: alpha(theme.palette.divider, 0.3),
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight="bold">{row.topic}</Typography>
                      {row.scope === 'always' && (
                        <Chip size="small" label="global" color="info" variant="outlined" sx={{ height: 18 }}/>
                      )}
                      {Array.isArray(row.tags) && row.tags.map(t => (
                        <Chip key={t} size="small" label={t} variant="outlined" sx={{ height: 18 }}/>
                      ))}
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
                      {row.content}
                    </Typography>
                    {row.last_updated_by && (
                      <Typography variant="caption" color="text.secondary">
                        {row.lastUpdated?.slice(0, 10)} · {row.last_updated_by}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(row)} title="Edit">
                      <EditIcon fontSize="small"/>
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row)} title="Delete" color="error">
                      <DeleteIcon fontSize="small"/>
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" startIcon={<AddIcon/>} onClick={openNew}>
                Add entry
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit profile entry' : 'Add profile entry'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Topic"
              required
              autoFocus
              value={editing?.topic || ''}
              onChange={e => setEditing({ ...editing, topic: e.target.value })}
              placeholder="WiFi Coverage"
              helperText="Short label — usually matches the audit Title"
            />
            <TextField
              label="Tags"
              value={editing?.tags || ''}
              onChange={e => setEditing({ ...editing, tags: e.target.value })}
              placeholder="wifi, heatmap"
              helperText="Comma-separated"
            />
            <TextField
              label="Content"
              required
              multiline rows={5}
              value={editing?.content || ''}
              onChange={e => setEditing({ ...editing, content: e.target.value })}
              placeholder="Heatmap completed 2026-03-15 by Ryan F. Covers main building and warehouse. North annex is a known dead spot — customer is aware."
            />
            <FormControl size="small">
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={editing?.scope || 'matched'}
                onChange={e => setEditing({ ...editing, scope: e.target.value })}
              >
                <MenuItem value="matched">Matched — only this audit (and others tagged below)</MenuItem>
                <MenuItem value="always">Always — every AI audit for this customer</MenuItem>
              </Select>
            </FormControl>
            {editing?.scope === 'matched' && (
              <TextField
                label="Audit function paths"
                value={(editing?.audit_function_paths || []).join('\n')}
                onChange={e => setEditing({
                  ...editing,
                  audit_function_paths: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                })}
                multiline rows={2}
                helperText="One Test-* function path per line — defaults to this audit"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16}/> : <SaveIcon/>}
            onClick={handleSave}
            disabled={saving || !editing?.topic || !editing?.content}
          >
            {saving ? 'Saving…' : (editing?.id ? 'Save' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default AccountProfileEditor;
